import React, { useState } from 'react';
import { Trophy, Medal, Crown, Filter, ChevronDown } from 'lucide-react';
import { AVP, MetricType } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { MovementIndicator } from './MovementIndicator';
interface LeaderboardSectionProps {
  avps: AVP[];
  onSelectAvp?: (avp: AVP) => void;
}
export function LeaderboardSection({
  avps,
  onSelectAvp
}: LeaderboardSectionProps) {
  const [metric, setMetric] = useState<MetricType>('revenue');
  const [divisionFilter, setDivisionFilter] = useState<string>('All');
  // Sort and filter logic
  const filteredAvps = avps.filter(avp => divisionFilter === 'All' || avp.division === divisionFilter);
  const sortedAvps = [...filteredAvps].sort((a, b) => {
    if (metric === 'revenue') return b.totalRevenue - a.totalRevenue;
    if (metric === 'volume') return b.loanVolume - a.loanVolume;
    return b.avgRevenue - a.avgRevenue;
  });
  // Re-assign ranks based on current sort for display purposes
  const displayAvps = sortedAvps.map((avp, index) => ({
    ...avp,
    displayRank: index + 1
  }));
  const top3 = displayAvps.slice(0, 3);
  const rest = displayAvps.slice(3);
  const getMetricValue = (avp: AVP) => {
    if (metric === 'revenue') return formatCurrency(avp.totalRevenue, true);
    if (metric === 'volume') return formatCurrency(avp.loanVolume, true);
    return formatCurrency(avp.avgRevenue);
  };
  const getMetricLabel = () => {
    if (metric === 'revenue') return 'Total Revenue';
    if (metric === 'volume') return 'Loan Volume';
    return 'Avg Revenue / Loan';
  };
  return <section className="mb-12" data-id="element-204">
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4" data-id="element-205">
        <div data-id="element-206">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2" data-id="element-207">
            <Trophy className="w-5 h-5 text-indigo-400" data-id="element-208" />
            Monthly Leaderboard
          </h2>
          <p className="text-sm text-slate-400 mt-1" data-id="element-209">
            Top performing AVPs and Directors
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-210">
          <div className="glass-panel rounded-lg p-1 flex text-sm" data-id="element-211">
            {(['revenue', 'volume', 'efficiency'] as MetricType[]).map(m => <button key={m} onClick={() => setMetric(m)} className={`px-3 py-1.5 rounded-md capitalize transition-all ${metric === m ? 'bg-indigo-500/20 text-indigo-300 font-medium' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-212">
                {m}
              </button>)}
          </div>

          <div className="relative group" data-id="element-213">
            <button className="glass-panel px-3 py-2 rounded-lg flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-id="element-214">
              <Filter className="w-4 h-4" data-id="element-215" />
              {divisionFilter}
              <ChevronDown className="w-4 h-4" data-id="element-216" />
            </button>
            <div className="absolute right-0 top-full mt-2 w-40 glass-panel rounded-xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20" data-id="element-217">
              {['All', 'Northeast', 'Southeast', 'Central', 'West', 'Mountain'].map(div => <button key={div} onClick={() => setDivisionFilter(div)} className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/10 rounded-lg transition-colors" data-id="element-218">
                  {div}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Spotlight - Podium order: 2nd, 1st, 3rd */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 md:items-end" data-id="element-219">
        {[top3[1], top3[0], top3[2]].filter(Boolean).map(avp => {
        const rank = avp.displayRank;
        const isFirst = rank === 1;
        const isSecond = rank === 2;
        const isThird = rank === 3;
        const cardStyle = isFirst ? 'bg-gradient-gold shadow-[0_0_30px_rgba(250,204,21,0.15)]' : isSecond ? 'bg-gradient-silver' : 'bg-gradient-bronze';
        const textStyle = isFirst ? 'text-gradient-gold' : isSecond ? 'text-gradient-silver' : 'text-gradient-bronze';
        const Icon = isFirst ? Crown : Medal;
        return <button key={avp.id} onClick={() => onSelectAvp?.(avp)} className={`rounded-3xl p-6 relative overflow-hidden flex flex-col items-center text-center border text-left w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400/40 ${cardStyle} ${isFirst ? 'md:-translate-y-8' : isSecond ? 'md:-translate-y-3' : ''}`} data-id="element-220">
              {/* Rank Badge */}
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center font-bold text-white border border-white/10 backdrop-blur-md" data-id="element-221">
                {avp.displayRank}
              </div>

              <div className="absolute top-4 right-4" data-id="element-222">
                <MovementIndicator currentRank={avp.displayRank} previousRank={avp.previousRank} data-id="element-223" />
              </div>

              <div className="relative mb-4 mt-2" data-id="element-224">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold bg-[#0f1423] border-2 ${isFirst ? 'border-yellow-500/50' : isSecond ? 'border-slate-400/50' : 'border-orange-500/50'} shadow-xl`} data-id="element-225">
                  {avp.initials}
                </div>
                <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#0f1423] border border-white/10 shadow-lg`} data-id="element-226">
                  <Icon className={`w-4 h-4 ${isFirst ? 'text-yellow-400' : isSecond ? 'text-slate-300' : 'text-orange-400'}`} data-id="element-227" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-white mt-2" data-id="element-228">{avp.name}</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-229">
                {avp.division} Division
              </p>

              <div className="w-full bg-black/20 rounded-xl p-4 border border-white/5" data-id="element-230">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1" data-id="element-231">
                  {getMetricLabel()}
                </p>
                <p className={`text-3xl font-bold ${textStyle}`} data-id="element-232">
                  {getMetricValue(avp)}
                </p>
              </div>

              <div className="w-full grid grid-cols-2 gap-2 mt-4" data-id="element-233">
                <div className="bg-black/10 rounded-lg p-2 border border-white/5" data-id="element-234">
                  <p className="text-xs text-slate-500" data-id="element-235">Loans</p>
                  <p className="text-sm font-semibold text-slate-200" data-id="element-236">
                    {formatNumber(avp.loansClosed)}
                  </p>
                </div>
                <div className="bg-black/10 rounded-lg p-2 border border-white/5" data-id="element-237">
                  <p className="text-xs text-slate-500" data-id="element-238">LOs</p>
                  <p className="text-sm font-semibold text-slate-200" data-id="element-239">
                    {avp.loCount}
                  </p>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Remaining Ranks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-240">
        {rest.map(avp => <button key={avp.id} onClick={() => onSelectAvp?.(avp)} className="glass-panel rounded-2xl p-4 flex items-center gap-4 text-left w-full cursor-pointer hover:border-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40" data-id="element-241">
            <div className="w-8 text-center font-bold text-slate-500" data-id="element-242">
              {avp.displayRank}
            </div>

            <div className="w-10 h-10 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-semibold text-sm shrink-0" data-id="element-243">
              {avp.initials}
            </div>

            <div className="flex-1 min-w-0" data-id="element-244">
              <h4 className="text-white font-medium truncate" data-id="element-245">{avp.name}</h4>
              <p className="text-xs text-slate-400 truncate" data-id="element-246">{avp.division}</p>
            </div>

            <div className="text-right" data-id="element-247">
              <p className="text-white font-semibold" data-id="element-248">{getMetricValue(avp)}</p>
              <div className="flex justify-end mt-1" data-id="element-249">
                <MovementIndicator currentRank={avp.displayRank} previousRank={avp.previousRank} data-id="element-250" />
              </div>
            </div>
          </button>)}
      </div>
    </section>;
}